import axios from 'axios';
import { cloneDeep } from 'lodash';
import {
  fileNameFrom, isJobActive, MAX_POLLS, POLL_INTERVAL_MS, receiptsFileName,
} from '@/utils/carrierImport';

// Portal accounts and their import runs — the state behind the Carriers screen's
// Portal accounts tab, one account's own history and the All updates drawer.
//
// Non-namespaced like the neighbouring modules; every action and getter is
// prefixed `carrierImport` so nothing collides. One owner and one poll: the tab,
// the account modal and the drawer all read the same runs.
//
// The Integrations → Allianz modal deliberately does NOT use this module. It
// keeps its own fetching, so this work cannot change how that screen behaves.

const defaultState = {
  loading: false,
  loaded: false,
  // scrape/account/list — every portal account of the tenant.
  accounts: [],
  // The portal catalog, straight from the backend (includes the unavailable
  // ones the picker greys out).
  portals: [],
  receiverAddress: '',
  // Tenant-wide runs, newest first. Both kinds: All updates shows everything.
  runs: { items: [], total: 0 },
  // scrapeJobId -> scrape/detail response (carries importSummary)
  runDetail: {},
  // scrapeAccountId -> that account's own runs, for its Import history window.
  // Separate from `runs` because it is a different, narrower list: the tenant-wide
  // one is capped at 50 across every carrier, so one account's older runs simply
  // are not in it.
  accountRuns: {},
  // Whose history window is open, so the poll below can keep exactly that list
  // fresh and no other. Null when none is.
  openAccountId: null,
  pollHandle: null,
  pollCount: 0,
};

const state = { ...cloneDeep(defaultState) };

const getters = {
  carrierImportLoading: () => state.loading,
  carrierImportLoaded: () => state.loaded,
  carrierImportAccounts: () => state.accounts,
  carrierImportPortals: () => state.portals,
  carrierImportReceiverAddress: () => state.receiverAddress,
  carrierImportRuns: () => state.runs,
  carrierImportRunDetail: () => state.runDetail,
  carrierImportAccountRuns: () => state.accountRuns,
  /** insuranceCompanyId -> that carrier's portal accounts. */
  carrierImportAccountsByCompany: () => {
    const byCompany = {};
    state.accounts.forEach((a) => {
      const key = a.insuranceCompanyId || 'none';
      if (!byCompany[key]) byCompany[key] = [];
      byCompany[key].push(a);
    });
    return byCompany;
  },
};

const mutations = {
  CARRIER_IMPORT_SET_LOADING(state, v) { state.loading = v; },
  CARRIER_IMPORT_SET_ACCOUNTS(state, payload) {
    state.accounts = payload.items || [];
    state.portals = payload.portals || [];
    state.receiverAddress = payload.receiverAddress || '';
    state.loaded = true;
  },
  CARRIER_IMPORT_SET_RUNS(state, page) {
    state.runs = { items: page.items || [], total: page.total || 0 };
  },
  CARRIER_IMPORT_SET_RUN_DETAIL(state, detail) {
    state.runDetail = { ...state.runDetail, [detail.scrapeJobId]: detail };
  },
  CARRIER_IMPORT_SET_ACCOUNT_RUNS(state, { scrapeAccountId, items }) {
    // Replaced, not mutated: Vue 2 does not see a key added to an object.
    state.accountRuns = { ...state.accountRuns, [scrapeAccountId]: items || [] };
  },
  CARRIER_IMPORT_SET_OPEN_ACCOUNT(state, scrapeAccountId) {
    state.openAccountId = scrapeAccountId;
  },
  CARRIER_IMPORT_SET_POLL(state, { handle, count }) {
    state.pollHandle = handle;
    state.pollCount = count;
  },
};

const actions = {
  carrierImportFetchAccounts({ commit }, silent) {
    if (!silent) commit('CARRIER_IMPORT_SET_LOADING', true);
    return axios.post('/insurance/policy/scrape/account/list', {})
      .then((res) => {
        commit('CARRIER_IMPORT_SET_ACCOUNTS', res.data || {});
        return res.data;
      })
      .finally(() => commit('CARRIER_IMPORT_SET_LOADING', false));
  },

  /**
   * Tenant-wide runs for All updates and for the "is this account busy right
   * now" line. One account's own history is fetched by the modal that shows it,
   * not cached here — it is a different, narrower list.
   */
  /**
   * Every client one read touched, one per person, paged and searchable
   * (scrape/client-changes): who it made or recognised and how, the RFC it put
   * right, and the policies it attached.
   */
  carrierImportFetchClientChanges(_, {
    scrapeJobId, searchQuery, limit, offset,
  }) {
    return axios.post('/insurance/policy/scrape/client-changes', {
      scrapeJobId, searchQuery, limit, offset,
    }).then((res) => res.data);
  },

  /** The same people as a CSV, one line per policy, built from the whole record. Resolves to a Blob. */
  carrierImportExportClientChanges(_, { scrapeJobId, searchQuery }) {
    return axios.post('/insurance/policy/scrape/client-changes/export', {
      scrapeJobId, searchQuery,
    }, { responseType: 'blob' }).then((res) => res.data);
  },

  carrierImportFetchRuns({ commit, dispatch }, params) {
    return axios.post('/insurance/policy/scrape/list', { limit: 50, ...(params || {}) })
      .then((res) => {
        commit('CARRIER_IMPORT_SET_RUNS', res.data || {});
        dispatch('carrierImportSyncPolling');
        return res.data;
      });
  },

  /**
   * One account's own history, for the window opened from its row.
   *
   * Kept in the store rather than in the component that shows it so the poll can
   * advance it: the previous local-array version was fetched once on open and
   * never again, so a run started from the Read dialog never appeared in an open
   * window and a code the portal was waiting for was never asked for.
   */
  carrierImportFetchAccountRuns({ commit, dispatch }, scrapeAccountId) {
    return axios.post('/insurance/policy/scrape/list', {
      scrapeAccountIds: [scrapeAccountId], limit: 50,
    }).then((res) => {
      commit('CARRIER_IMPORT_SET_ACCOUNT_RUNS', {
        scrapeAccountId,
        items: (res.data || {}).items,
      });
      dispatch('carrierImportSyncPolling');
      return res.data;
    });
  },

  /** Which account's history is on screen; null when the window closes. */
  carrierImportSetOpenAccount({ commit }, scrapeAccountId) {
    commit('CARRIER_IMPORT_SET_OPEN_ACCOUNT', scrapeAccountId || null);
  },

  /**
   * The staged rows of a snapshot run — what it found and has not written.
   *
   * Not cached in the store: the review modal is the only thing that reads them,
   * it is open on one run at a time, and a book's worth of staged rows has no
   * business living in the store after it closes.
   */
  carrierImportFetchSnapshot(_, {
    scrapeJobId, scrapeAccountId, kinds, reviewStatuses, skipReasons, excludeSkipReasons, limit, offset,
  }) {
    // One run, or every run of one account (the cumulative review): whichever is given.
    return axios.post('/insurance/policy/scrape/snapshot/list', {
      scrapeJobId, scrapeAccountId, kinds, reviewStatuses, skipReasons, excludeSkipReasons, limit, offset,
    }).then((res) => res.data);
  },

  /**
   * One staged row, with its payload and a freshly computed diff.
   *
   * Separate from the list because the list omits the payload on purpose — it is
   * the whole scraped policy and a page of 200 would be megabytes — and because
   * the diff is recomputed here against the book as it is now rather than
   * replayed from what staging recorded.
   */
  carrierImportFetchSnapshotDetail(_, id) {
    return axios.post('/insurance/policy/scrape/snapshot/detail', { id })
      .then((res) => res.data);
  },

  /**
   * Every row of a group, as ids and nothing else.
   *
   * Its own endpoint, not a mode on the list: what makes the list expensive is
   * the `changes` array per row, which select-all has no use for. One request
   * instead of thirteen pages on a 2,545-policy run.
   */
  carrierImportFetchSnapshotIds(_, {
    scrapeJobId, scrapeAccountId, kinds, skipReasons, excludeSkipReasons,
  }) {
    return axios.post('/insurance/policy/scrape/snapshot/ids', {
      scrapeJobId, scrapeAccountId, kinds, skipReasons, excludeSkipReasons,
    }).then((res) => res.data);
  },

  /**
   * A group of staged rows as a CSV, filtered as the list is -- the carrier's own record for
   * each. Resolves to the file's Blob.
   */
  carrierImportExportSnapshot(_, {
    scrapeJobId, scrapeAccountId, kinds, reviewStatuses, skipReasons, excludeSkipReasons,
  }) {
    return axios.post('/insurance/policy/scrape/snapshot/export', {
      scrapeJobId, scrapeAccountId, kinds, reviewStatuses, skipReasons, excludeSkipReasons,
    }, { responseType: 'blob' }).then((res) => res.data);
  },

  /**
   * One run's figures, or -- with no id -- the tenant's pending work per portal
   * account across every read (`byAccount`), which "Need your input" lists.
   */
  carrierImportFetchSnapshotSummary(_, scrapeJobId) {
    return axios.post('/insurance/policy/scrape/snapshot/summary', { scrapeJobId: scrapeJobId || null })
      .then((res) => res.data);
  },

  /**
   * Create or update the policies behind these staged rows.
   *
   * Takes the ids, or `{ ids, services }`: for a row applied from its own modal,
   * `services` is `[{ id, names }]`, the services someone ticked one by one to
   * add, update or remove. Only those are touched; a row without an entry, and
   * every bulk apply from the list, leaves the policy's services alone.
   */
  carrierImportApplySnapshot({ dispatch }, payload) {
    const { ids, services } = Array.isArray(payload) ? { ids: payload } : payload;
    const body = { ids };
    const picked = (services || []).filter((s) => s && s.id && s.names && s.names.length);
    if (picked.length) body.services = picked.map((s) => ({ id: s.id, names: s.names }));
    return axios.post('/insurance/policy/scrape/snapshot/apply', body)
      .then((res) => {
        // The run's counts do not move, but a person who just applied wants the
        // list to agree with what they did.
        dispatch('carrierImportFetchRuns');
        return res.data;
      });
  },

  /**
   * Name the client for a staged policy the carrier gave no RFC, e-mail or
   * phone for. The row becomes a new policy for that person, waiting to apply.
   */
  carrierImportAssignSnapshotClient(_, { id, personProfileId }) {
    return axios.post('/insurance/policy/scrape/snapshot/assign-client', { id, personProfileId })
      .then((res) => res.data);
  },

  /** Leave them out of the book, and stop offering them. */
  carrierImportDismissSnapshot({ dispatch }, ids) {
    return axios.post('/insurance/policy/scrape/snapshot/dismiss', { ids })
      .then((res) => {
        dispatch('carrierImportFetchRuns');
        return res.data;
      });
  },

  /**
   * What the carrier's payment report proposed for one run, and what is left.
   *
   * The reconcile itself has been staging these all along — every report line
   * lands with a proposed action and applies nothing until a person says so.
   * Until now nothing in this app asked.
   */
  carrierImportFetchPaymentSummary(_, scrapeJobId) {
    return axios.post('/insurance/policy/payment/reconcile/allianz/summary', { scrapeJobId })
      .then((res) => res.data);
  },

  /**
   * Every receipt a payments read saw, with what became of its policy, as a CSV. Returns `{ blob, fileName }`, the name the server's
   * when it sends one.
   */
  carrierImportExportRefundReceipts(_, { scrapeJobId, jobType }) {
    return axios.post('/insurance/policy/payment/reconcile/allianz/receipts/export', {
      scrapeJobId,
    }, { responseType: 'blob' }).then((res) => ({
      blob: res.data,
      fileName: fileNameFrom(
        res.headers && res.headers['content-disposition'],
        receiptsFileName(jobType),
      ),
    }));
  },

  /**
   * One group's rows, paged by the server.
   *
   * `groups` is filtered server-side now. It used to be applied here, after the
   * fact, which made `totalCount` the count of a different set than the rows
   * beside it: "Load more" paged off the filtered length against an unfiltered
   * total and never stopped asking.
   *
   * `outcomes`, `matchState` (MATCHED | UNMATCHED) and `searchQuery` are the
   * server's too, for a read's full policy list (PaymentRunPoliciesModal).
   */
  carrierImportFetchPaymentRows(_, {
    scrapeJobId, reviewStatuses, groups, outcomes, matchState, searchQuery, page, limit
  }) {
    return axios.post('/insurance/policy/payment/reconcile/allianz/list', {
      scrapeJobId, reviewStatuses, groups, outcomes, matchState, searchQuery, page, limit,
    }).then((res) => res.data);
  },

  /**
   * What accepting one row would really do, recomputed against the schedule as
   * it is now rather than replayed from what the read recorded.
   *
   * The list can say "the carrier collected up to 5 August"; only this says
   * which receipts that settles, which is the thing being agreed to.
   */
  /**
   * Every pending row of one group, as ids.
   *
   * Select-all means the group, not the page. Its own endpoint because the list
   * carries each row's detail, which select-all has no use for.
   */
  carrierImportFetchPaymentIds(_, { scrapeJobId, groups }) {
    return axios.post('/insurance/policy/payment/reconcile/allianz/ids', { scrapeJobId, groups })
      .then((res) => res.data);
  },

  carrierImportFetchPaymentDetails(_, ids) {
    return axios.post('/insurance/policy/payment/reconcile/allianz/detail', { ids })
      .then((res) => (res.data && res.data.items) || []);
  },

  /**
   * Carry out or set aside the carrier's proposals.
   *
   * ACCEPT marks the receipts paid, or deactivates the policy the carrier
   * stopped collecting on. Anything that fails stays pending and says why, per
   * row — so the caller refreshes rather than assuming.
   */
  carrierImportResolvePayments({ dispatch }, { ids, decision, idempotencyKey }) {
    return axios.post('/insurance/policy/payment/reconcile/allianz/resolve', {
      ids, decision, idempotencyKey,
    })
      .then((res) => {
        dispatch('carrierImportFetchRuns');
        return res.data;
      });
  },

  carrierImportFetchRunDetail({ commit }, scrapeJobId) {
    return axios.post('/insurance/policy/scrape/detail', { scrapeJobId })
      .then((res) => {
        commit('CARRIER_IMPORT_SET_RUN_DETAIL', res.data);
        return res.data;
      });
  },

  /**
   * Start a read. One call per kind: "both" is two, payments first — a failed
   * policy crawl keeps its message on the queue for continuation attempts, and
   * the payment report would otherwise wait behind them.
   */
  /** `readId`: the read this job is one half of, when payments and policies are started together. */
  carrierImportStart(_, {
    scrapeAccountId, kind, readId, dryRun,
  }) {
    return axios.post('/insurance/policy/scrape/start', {
      scrapeAccountId, kind, readId, dryRun,
    })
      .then((res) => res.data);
  },

  /**
   * When an account reads by itself: `{ scrapeAccountId, enabled, frequency, time,
   * weekdays, monthDay, kinds }`. Resolves to the saved account.
   */
  carrierImportSaveSchedule({ dispatch }, payload) {
    return axios.post('/insurance/policy/scrape/account/schedule', payload)
      .then((res) => dispatch('carrierImportFetchAccounts', true).then(() => res.data));
  },

  /** A new portal password: its own endpoint, never part of the account form. */
  carrierImportUpdatePassword({ dispatch }, { scrapeAccountId, password }) {
    return axios.post('/insurance/policy/scrape/account/password', { scrapeAccountId, password })
      .then((res) => dispatch('carrierImportFetchAccounts', true).then(() => res.data));
  },

  /**
   * Re-check the rows still waiting against the book as it is now: a row whose
   * change Renova already holds (or that an older version of the review proposed)
   * is closed as up to date, the rest get today's diff and reasons. One account,
   * or the whole tenant without one. Null when the backend cannot (an older one):
   * the caller just carries on with what is stored.
   */
  carrierImportRecheckSnapshots(_, scrapeAccountId) {
    return axios.post('/insurance/policy/scrape/snapshot/recheck', { scrapeAccountId: scrapeAccountId || null })
      .then((res) => res.data || null)
      .catch(() => null);
  },

  /**
   * What each portal account still asks of a person: its policy work across reads
   * (snapshot/summary with no id) and its latest payments read's, by group.
   * Either half may fail on its own; the drawer shows what came back.
   */
  carrierImportFetchPendingByAccount() {
    return Promise.all([
      axios.post('/insurance/policy/scrape/snapshot/summary', {})
        .then((res) => res.data || {})
        .catch(() => ({})),
      axios.post('/insurance/policy/payment/reconcile/pending-by-account', {})
        .then((res) => (res.data && res.data.items) || [])
        .catch(() => null),
    ]).then(([summary, payments]) => ({
      policies: summary.byAccount || [],
      // Tenant-wide, like the badge: not one read's.
      clientChanges: summary.clientChangesPendingCount,
      payments,
    }));
  },

  /**
   * Ask the backend to stop a run.
   *
   * Best-effort by design: this writes the intent to the job row and the worker
   * picks it up at its next checkpoint call, which is between clients. Nothing
   * cancels the queued message, so the run stays "stopping" until the worker
   * confirms — the UI must not claim otherwise.
   */
  carrierImportStop({ dispatch }, scrapeJobId) {
    return axios.post('/insurance/policy/scrape/cancel', { scrapeJobId })
      .then((res) => {
        dispatch('carrierImportFetchRuns');
        return res.data;
      });
  },

  /**
   * Pick a stopped or failed run back up under its own id. The checkpoint makes
   * it skip the clients already crawled, so this continues rather than restarts.
   */
  carrierImportProceed({ dispatch }, scrapeJobId) {
    return axios.post('/insurance/policy/scrape/resume', { scrapeJobId })
      .then((res) => {
        dispatch('carrierImportFetchRuns');
        return res.data;
      });
  },

  /**
   * Polls only while something is moving, and gives up after MAX_POLLS so a job
   * wedged upstream cannot keep the tab calling the API forever.
   *
   * A failed poll stops the loop immediately rather than waiting out the cap.
   * Whether to keep polling is decided from the runs we just fetched, so once a
   * fetch fails that decision is being made on stale state: it would keep
   * firing every seven seconds for the full twenty-one minutes, against an API
   * that is already failing, and throw an unhandled rejection each time.
   */
  carrierImportSyncPolling({ commit, dispatch }) {
    // The open account's own runs count towards "is anything moving": its older
    // runs may not be in the tenant-wide 50 at all, and that window is exactly
    // where someone watches one account finish.
    const openRuns = state.openAccountId ? state.accountRuns[state.openAccountId] || [] : [];
    const anyActive = (state.runs.items || []).concat(openRuns).some(isJobActive);
    if (anyActive && !state.pollHandle) {
      const handle = setInterval(() => {
        if (state.pollCount >= MAX_POLLS) {
          dispatch('carrierImportStopPolling');
          return;
        }
        commit('CARRIER_IMPORT_SET_POLL', { handle: state.pollHandle, count: state.pollCount + 1 });
        Promise.all([
          dispatch('carrierImportFetchRuns'),
          dispatch('carrierImportFetchAccounts', true),
          state.openAccountId
            ? dispatch('carrierImportFetchAccountRuns', state.openAccountId)
            : Promise.resolve(),
        ]).catch(() => dispatch('carrierImportStopPolling'));
      }, POLL_INTERVAL_MS);
      commit('CARRIER_IMPORT_SET_POLL', { handle, count: 0 });
    } else if (!anyActive && state.pollHandle) {
      dispatch('carrierImportStopPolling');
    }
  },

  carrierImportStopPolling({ commit }) {
    if (state.pollHandle) clearInterval(state.pollHandle);
    commit('CARRIER_IMPORT_SET_POLL', { handle: null, count: 0 });
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
